import styled, { css } from 'styled-components';

import { mediaUp, rem } from 'approot/shared/style';
import {
  grey1,
  grey5,
  grey6,
  grey10,
  grey3,
  purple7,
} from 'approot/shared/styles/colours.style';
import {
  spacing4,
  spacing16,
  spacing24,
  spacing32,
  spacing20,
} from 'approot/shared/styles/spacing.style';
import { FileDropperImageField } from 'lib/form/file-dropper/file-dropper-image.style';
import {
  font14,
  fontSize14,
  fontSize16,
  fontSize28,
  fontWeightBold,
} from 'approot/shared/styles/typography.style';
import {
  flexCentreAlignCss,
  baseFlexCss,
  flexLeftAlignCss,
  flexSpaceBetweenCss,
} from 'approot/shared/styles/flex.style';
import {
  ACTIVITY_BUILDER_FOOTER_HEIGHT,
  ACTIVITY_BUILDER_HEADER_HEIGHT,
} from 'approot/activity-builder/activity-builder.constants';
import { PrimaryButton } from 'approot/shared/styles/buttons/primary-button.style';
import { bigBoxShadow } from 'approot/shared/styles/borders.style';
import { SecondaryButtonAndIconLink } from 'approot/shared/styles/buttons/secondary-button.style';

export const bigButtonCss = css`
  ${flexCentreAlignCss}
  ${bigBoxShadow}

  border-radius: ${rem(288)};
  font-size: ${rem(fontSize28)};
  font-weight: ${fontWeightBold};
  height: auto;
  padding: ${rem(24)} ${rem(40)};
`;

type IntroBladeProps = {
  $noHeaderFooter?: boolean;
};

type IntroSlideProps = {
  $hasBanner?: boolean;
};
export const IntroSlide = styled.div<IntroSlideProps>`
  ${baseFlexCss}
  flex-direction: column;
  justify-content: flex-start;
  height: 100%;

  ${({ $hasBanner }) =>
    $hasBanner &&
    `
    & > div {
      margin-top: ${rem(180)};
      ${mediaUp.sm`
         margin-top: 0;
      `}
    }
  `}
`;

type IntroSlidePrintProps = {
  $backgroundImage?: string;
};

export const IntroSlidePrint = styled.div<IntroSlidePrintProps>`
  height: 100%;
  position: relative;

  ${({ $backgroundImage }) =>
    $backgroundImage &&
    css`
      background-image: url(${$backgroundImage});
      background-size: cover;
      background-repeat: no-repeat;
      background-position: center;
    `}
`;

export const IntroImageUpload = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  padding: ${rem(spacing24)} 0 0 0;
  position: relative;

  ${FileDropperImageField} {
    height: 100%;
    margin: 0;
    max-width: none;
    border: none;
    background-color: transparent;
  }
`;

type IntroTextWrapperProps = {
  $showBoxShadow?: boolean;
};

export const IntroTextWrapper = styled.div<IntroTextWrapperProps>`
  ${bigBoxShadow}
  background-color: rgba(255, 255, 255, 0.8);
  border-radius: ${rem(24)};
  max-width: ${rem(825)};
  margin: ${rem(120)} ${rem(spacing24)};
  padding: ${rem(spacing32)};
  position: relative;

  ${mediaUp.sm`
    margin: ${rem(128)} ${rem(spacing24)};
  `}
`;

export const IntroTextWrapperPrint = styled.div`
  background-color: ${grey1};
  border-radius: ${rem(24)};
  max-width: ${rem(825)};
  margin: ${rem(spacing24)};
  padding: ${rem(spacing32)};
  position: relative;

  ${mediaUp.sm`
    margin: ${rem(128)} ${rem(spacing24)} 0 ${rem(spacing24)};
  `}
`;

export const IntroBladePrint = styled.div<IntroBladeProps>`
  ${flexCentreAlignCss}
  align-items: flex-start;
  position: relative;
  height: calc(
    100% -
      ${rem(ACTIVITY_BUILDER_HEADER_HEIGHT + ACTIVITY_BUILDER_FOOTER_HEIGHT)}
  );
  overflow: hidden;

  ${IntroTextWrapperPrint} {
    margin: ${rem(spacing24)} auto 0;
  }
`;

export const IntroTitle = styled.h1`
  color: ${grey10};
  font-size: ${rem(28)};
  line-height: ${rem(40)};
  font-weight: ${fontWeightBold};
  text-align: center;
  margin: 0;
  ${mediaUp.sm`
    font-size: ${rem(40)};
    line-height: ${rem(56)};
  `}
`;

export const IntroHeaderMeta = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  z-index: 1;
`;

export const IntroHeaderMetaContent = styled.div`
  display: flex;
  justify-content: space-between;
  position: relative;
  padding: 0 ${rem(spacing24)};
`;

export const IntroHeaderPill = styled.div`
  ${font14}
  background-color: ${grey1};
  border: 1px solid ${grey5};
  border-radius: ${rem(24)};
  color: ${grey6};
  display: inline-block;
  padding: ${rem(spacing4)} ${rem(spacing16)};
  white-space: nowrap;

  strong {
    color: ${grey10};
    margin-left: ${rem(spacing16)};
  }
`;

export const IntroHeader = styled.div`
  ${flexCentreAlignCss}
  background-color: ${grey1};
  border-bottom: 1px solid ${grey5};
  height: ${rem(ACTIVITY_BUILDER_HEADER_HEIGHT)};
  padding-top: 0;
  padding-bottom: 0;
  position: relative;
  svg {
    height: ${rem(40)};
    width: ${rem(120)};
    position: relative;
  }
`;

export const IntroFooter = styled.div`
  ${flexSpaceBetweenCss}
  background-color: ${grey1};
  height: ${rem(ACTIVITY_BUILDER_FOOTER_HEIGHT)};
  padding-top: 0;
  padding-bottom: 0;
  position: relative;
`;

export const IntroFooterPageNumber = styled.div`
  ${flexCentreAlignCss}
  width: ${rem(32)};
  height: ${rem(32)};
  border-radius: 50%;
  border: 1px solid ${grey5};
  font-size: ${rem(fontSize14)};
  line-height: ${rem(32)};
`;

export const IntroFooterSubject = styled.p`
  margin: 0;
  font-size: ${rem(fontSize16)};
  line-height: ${rem(ACTIVITY_BUILDER_FOOTER_HEIGHT)};
`;

export const IntroFooterAttribution = styled.div`
  ${flexCentreAlignCss}
  position: absolute;
  right: 0;
  left: 0;
  bottom: 0;
  top: 0;
`;

export const IntroFooterPageWrapper = styled.div`
  ${flexLeftAlignCss}

  svg {
    height: ${rem(20)};
    width: ${rem(60)};
    position: relative;
    margin-right: ${rem(spacing16)};
  }
`;

export const IntroStartWrapper = styled.div`
  ${flexCentreAlignCss}
  padding: 0 ${rem(spacing20)} ${rem(52)} ${rem(spacing20)};

  @media (min-height: 500px) {
    left: 0;
    right: 0;
    bottom: ${rem(120)};
    padding: 0 ${rem(spacing20)};
    position: absolute;
  }
`;

export const IntroStartButton = styled(PrimaryButton)`
  ${bigButtonCss}
`;

export const IntroStartButtonView = styled.div`
  ${bigBoxShadow}
  background-color: ${grey3};
  border-radius: ${rem(288)};
  height: ${rem(88)};
  width: ${rem(235)};
`;

export const IntroAddStudentsButton = styled(SecondaryButtonAndIconLink)`
  border-color: ${purple7};
  color: ${purple7};

  svg {
    stroke: ${purple7};
    fill: none;
  }

  &:hover {
    color: ${purple7};
    svg {
      stroke: ${purple7};
      fill: none;
    }
  }

  &:focus {
    color: ${purple7};
    svg {
      stroke: ${purple7};
      fill: none;
    }
  }
`;
